import { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Modal, SpinLoading, Toast } from 'antd-mobile';
import { AddCircleOutline, SearchOutline } from 'antd-mobile-icons';
import Avatar from '@/components/Avatar';
import EmptyState from '@/components/EmptyState';
import Skeleton from '@/components/Skeleton';
import { Customer } from '@/types';
import { createCustomer, getCustomers } from '@/api/customers';
import useBackHandler, { BACK_PRIORITY } from '@/hooks/useBackHandler';
import './styles.css';

interface CustomerSelectorProps {
  selectedId?: number;
  onSelect: (customer: Customer) => void;
}

const PINYIN_INITIALS = 'ABCDEFGHJKLMNOPQRSTWXYZ'.split('');
const PINYIN_BOUNDARIES = '阿八嚓咑妸发旮哈讥咔垃妈拏噢妑七呥仨他哇夕丫帀'.split('');
const SURNAME_OVERRIDES: Record<string, string> = {
  曾: 'Z',
  查: 'Z',
  单: 'S',
  区: 'O',
  仇: 'Q',
  解: 'X',
  朴: 'P'
};

function getPinyinInitial(value: string) {
  const first = value.trim().charAt(0);
  if (!first) return '#';
  if (/^[a-z]$/i.test(first)) return first.toUpperCase();
  if (/^[0-9]$/.test(first)) return '#';
  if (SURNAME_OVERRIDES[first]) return SURNAME_OVERRIDES[first];

  for (let index = PINYIN_BOUNDARIES.length - 1; index >= 0; index -= 1) {
    if (first.localeCompare(PINYIN_BOUNDARIES[index], 'zh-CN') >= 0) {
      return PINYIN_INITIALS[index];
    }
  }
  return '#';
}

export default function CustomerSelector({ selectedId, onSelect }: CustomerSelectorProps) {
  const [keyword, setKeyword] = useState('');
  const [debouncedKeyword, setDebouncedKeyword] = useState('');
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const groupRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [activeSurname, setActiveSurname] = useState('');
  const [showSurnameToast, setShowSurnameToast] = useState(false);
  const toastTimer = useRef<number | null>(null);

  const [modalVisible, setModalVisible] = useState(false);
  const [newName, setNewName] = useState('');
  const [newVillage, setNewVillage] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [creating, setCreating] = useState(false);
  const [formErrors, setFormErrors] = useState<{ name?: string }>({});

  useBackHandler(modalVisible, closeAddModal, BACK_PRIORITY.modal);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedKeyword(keyword), 300);
    return () => window.clearTimeout(timer);
  }, [keyword]);

  useEffect(() => {
    let cancelled = false;
    const isInitial = customers.length === 0 && !debouncedKeyword;
    if (isInitial) {
      setInitialLoading(true);
    } else {
      setSearching(true);
    }

    getCustomers({ keyword: debouncedKeyword })
      .then((res) => {
        if (!cancelled && res.code === 0) {
          setCustomers(res.data.list);
        }
      })
      .catch((error: { code?: string }) => {
        if (error?.code !== 'ERR_CANCELED') {
          Toast.show({ content: '加载客户失败', icon: 'fail' });
        }
      })
      .finally(() => {
        if (!cancelled) {
          setInitialLoading(false);
          setSearching(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [debouncedKeyword]);

  const grouped = useMemo(() => {
    const map = new Map<string, Customer[]>();
    customers.forEach((customer) => {
      const initial = getPinyinInitial(customer.surname || customer.name);
      const list = map.get(initial) || [];
      list.push(customer);
      map.set(initial, list);
    });

    const selectedCustomer = customers.find((customer) => customer.id === selectedId);
    const selectedInitial = selectedCustomer
      ? getPinyinInitial(selectedCustomer.surname || selectedCustomer.name)
      : '';

    return Array.from(map.entries())
      .sort(([left], [right]) => {
        if (left === selectedInitial) return -1;
        if (right === selectedInitial) return 1;
        if (left === '#') return 1;
        if (right === '#') return -1;
        return left.localeCompare(right, 'en');
      })
      .map(([surname, list]) => ({ surname, list }));
  }, [customers, selectedId]);

  const surnames = useMemo(() => grouped.map((group) => group.surname), [grouped]);

  useEffect(() => {
    if (surnames.length > 0 && !surnames.includes(activeSurname)) {
      setActiveSurname(surnames[0]);
    }
  }, [activeSurname, surnames]);

  const showIndexToast = (surname: string) => {
    setActiveSurname(surname);
    setShowSurnameToast(true);
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setShowSurnameToast(false), 600);
  };

  const updateActiveSurname = () => {
    const container = listRef.current;
    if (!container || grouped.length === 0) return;
    const top = container.scrollTop + 12;
    let current = grouped[0].surname;
    for (const { surname } of grouped) {
      const element = groupRefs.current[surname];
      if (element && element.offsetTop <= top) current = surname;
    }
    if (current !== activeSurname) showIndexToast(current);
  };

  useEffect(() => () => {
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
  }, []);

  const scrollToSurname = (surname: string) => {
    const element = groupRefs.current[surname];
    if (element && listRef.current) {
      listRef.current.scrollTo({ top: element.offsetTop, behavior: 'smooth' });
      showIndexToast(surname);
    }
  };

  const openAddModal = () => {
    setNewName(keyword);
    setNewVillage('');
    setNewPhone('');
    setFormErrors({});
    setModalVisible(true);
  };

  function closeAddModal() {
    setModalVisible(false);
    setNewName('');
    setNewVillage('');
    setNewPhone('');
    setFormErrors({});
  }

  const validateForm = () => {
    const errors: { name?: string } = {};
    if (!newName.trim()) errors.name = '请输入姓名';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateCustomer = async () => {
    if (!validateForm()) {
      Toast.show({ content: '请完善信息', icon: 'fail' });
      return;
    }
    setCreating(true);
    try {
      const res = await createCustomer({
        name: newName.trim(),
        village: newVillage.trim(),
        phone: newPhone.trim() || undefined
      });
      if (res.code === 0) {
        Toast.show({ content: '添加成功', icon: 'success' });
        onSelect(res.data);
        closeAddModal();
      } else {
        Toast.show({ content: res.message || '添加失败', icon: 'fail' });
      }
    } catch (error) {
      console.error(error);
      Toast.show({ content: '添加失败', icon: 'fail' });
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="customer-selector">
      <label className="customer-selector__search">
        <SearchOutline aria-hidden="true" />
        <input
          type="search"
          placeholder="搜索姓名、村或手机号"
          value={keyword}
          onChange={(event) => setKeyword(event.target.value)}
          aria-label="搜索客户"
        />
        {keyword && (
          <button type="button" onClick={() => setKeyword('')}>
            清除
          </button>
        )}
      </label>

      <div className="customer-selector__main">
        {initialLoading ? (
          <div className="customer-selector__loading"><Skeleton count={6} /></div>
        ) : customers.length === 0 && !searching ? (
          <EmptyState title="未找到客户" description="换个关键词，或快速添加新客户" />
        ) : (
          <>
            {searching && (
              <div className="customer-selector__searching" aria-label="正在搜索">
                <SpinLoading color="primary" style={{ '--size': '22px' } as React.CSSProperties} />
              </div>
            )}
            <div
              ref={listRef}
              className="customer-selector__list"
              onScroll={updateActiveSurname}
            >
              {grouped.map(({ surname, list }) => (
                <div
                  key={surname}
                  ref={(element) => {
                    groupRefs.current[surname] = element;
                  }}
                  className="customer-selector__group"
                  data-surname={surname}
                >
                  <div className="customer-selector__group-label">{surname}</div>
                  {list.map((customer) => {
                    const selected = selectedId === customer.id;
                    return (
                      <motion.button
                        key={customer.id}
                        type="button"
                        className="customer-selector__row"
                        onClick={() => onSelect(customer)}
                        whileTap={{ scale: 0.99 }}
                        aria-pressed={selected}
                        data-haptic="selection"
                      >
                        <Avatar
                          src={customer.avatar}
                          name={customer.name}
                          size={44}
                          className="customer-selector__avatar"
                        />
                        <span className="customer-selector__identity">
                          <strong>{customer.name}</strong>
                          <small className="customer-selector__meta">
                            <span>{customer.village || '未填写村庄'}</span>
                            {customer.phone ? <span className="customer-selector__phone">{customer.phone}</span> : null}
                          </small>
                        </span>
                        <span
                          className={`customer-selector__radio${selected ? ' is-selected' : ''}`}
                          aria-hidden="true"
                        >
                          {selected && (
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          )}
                        </span>
                      </motion.button>
                    );
                  })}
                </div>
              ))}
            </div>

            {surnames.length > 0 && (
              <nav className="customer-selector__index" aria-label="客户首字母索引">
                {surnames.map((surname) => (
                  <button
                    key={surname}
                    type="button"
                    className={activeSurname === surname ? 'is-active' : ''}
                    onClick={() => scrollToSurname(surname)}
                    data-haptic="selection"
                  >
                    <span>{surname}</span>
                    {activeSurname === surname && <i aria-hidden="true" />}
                  </button>
                ))}
              </nav>
            )}

            <AnimatePresence>
              {showSurnameToast && activeSurname && (
                <motion.div
                  className="customer-selector__index-toast"
                  initial={{ opacity: 0, scale: 0.86 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.86 }}
                >
                  {activeSurname}
                </motion.div>
              )}
            </AnimatePresence>
          </>
        )}
      </div>

      <div className="customer-selector__footer">
        <button type="button" onClick={openAddModal} data-haptic="medium">
          <AddCircleOutline aria-hidden="true" />
          <span>快速添加客户</span>
        </button>
      </div>

      <Modal
        visible={modalVisible}
        title="快速添加客户"
        content={
          <div className="customer-selector__modal-form">
            <label>
              <span>姓名 <b>*</b></span>
              <input
                className="ios-input"
                value={newName}
                onChange={(event) => {
                  setNewName(event.target.value);
                  if (formErrors.name) setFormErrors({});
                }}
                placeholder="例如：张三"
              />
              {formErrors.name && <small>{formErrors.name}</small>}
            </label>
            <label>
              <span>村</span>
              <input
                className="ios-input"
                value={newVillage}
                onChange={(event) => setNewVillage(event.target.value)}
                placeholder="例如：石榴村（可选）"
              />
            </label>
            <label>
              <span>电话</span>
              <input
                className="ios-input"
                type="tel"
                value={newPhone}
                onChange={(event) => setNewPhone(event.target.value)}
                placeholder="可选"
              />
            </label>
          </div>
        }
        closeOnAction
        onClose={closeAddModal}
        actions={[
          { key: 'cancel', text: '取消', onClick: closeAddModal },
          {
            key: 'confirm',
            text: creating ? '保存中' : '保存',
            primary: true,
            disabled: creating,
            onClick: handleCreateCustomer
          }
        ]}
      />
    </div>
  );
}
